import { Route, Switch } from 'react-router';
import { useAllowPreview } from 'utils/hooks/useAllowPreview';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import * as t from './actionTypes';
import * as actions from './actions';
import { BillingPage } from './components/Index/BillingPage';
import { BillingUsagePage } from './components/Index/BillingUsagePage';
import { CatalogProjectUsage } from './components/ProjectBreakdown/CatalogProjectUsage';
import { CatalogUsage } from './components/ProjectBreakdown/CatalogUsage';
import { CustomEventsProjectUsage } from './components/ProjectBreakdown/CustomEventsProjectUsage';
import { CustomEventsUsage } from './components/ProjectBreakdown/CustomEventsUsage';
import { JourneyEventsProjectUsage } from './components/ProjectBreakdown/JourneyEventsProjectUsage';
import { JourneyEventsProjectUsageByProject } from './components/ProjectBreakdown/JourneyEventsProjectUsageByProject';
import { JourneyEventsUsage } from './components/ProjectBreakdown/JourneyEventsUsage';
import { JourneyEventsUsageByProject } from './components/ProjectBreakdown/JourneyEventsUsageByProject';
import { MessagingProjectUsage } from './components/ProjectBreakdown/MessagingProjectUsage';
import { MessagingUsage } from './components/ProjectBreakdown/MessagingUsage';
import { SmartIngestProjectUsage } from './components/ProjectBreakdown/SmartIngestProjectUsage';
import { SmartIngestUsage } from './components/ProjectBreakdown/SmartIngestUsage';
import { UsersProjectUsage } from './components/ProjectBreakdown/UsersProjectUsage';
import { UsersUsage } from './components/ProjectBreakdown/UsersUsage';
import { USAGE_LIMITS_FF, USAGE_LIMITS_PREVIEW_KEY } from './constants';
import { BillingQueryContextProvider } from './contexts';
import { DateContextProvider } from './contexts/DateContextProvider';
import { HightouchBillingQueryContextProvider } from './contexts/HightouchBillingQueryContextProvider';
import { ProjectQueryContextProvider } from './contexts/ProjectQueryContextProvider';
import { QueryContextProvider } from './contexts/QueryContextProvider';
import { UsageLimitsContextProvider } from './contexts/UsageLimitsContextProvider';

export { default as billingReducer } from './reducer';
export * from './sagas';
export { actions, t };

const BillingWithUsageLimits = () => {
  return (
    <DateContextProvider>
      <UsageLimitsContextProvider>
        <QueryContextProvider>
          <ProjectQueryContextProvider>
            <HightouchBillingQueryContextProvider>
              <Switch>
                <Route exact path="/payments/info">
                  <BillingUsagePage />
                </Route>
                <Route exact path="/payments/info/messagingUsage">
                  <MessagingProjectUsage />
                </Route>
                <Route exact path="/payments/info/dataUsage">
                  <CustomEventsProjectUsage />
                </Route>
                <Route exact path="/payments/info/journeyEventsUsage">
                  <JourneyEventsProjectUsage />
                </Route>
                <Route exact path="/payments/info/journeyEventsUsage/:projectId">
                  <JourneyEventsProjectUsageByProject />
                </Route>
                <Route exact path="/payments/info/audienceUsage">
                  <UsersProjectUsage />
                </Route>
                <Route exact path="/payments/info/catalogUsage">
                  <CatalogProjectUsage />
                </Route>
                <Route exact path="/payments/info/smartIngestUsage">
                  <SmartIngestProjectUsage />
                </Route>
              </Switch>
            </HightouchBillingQueryContextProvider>
          </ProjectQueryContextProvider>
        </QueryContextProvider>
      </UsageLimitsContextProvider>
    </DateContextProvider>
  );
};

const BillingWithoutUsageLimits = () => {
  return (
    <UsageLimitsContextProvider>
      <BillingQueryContextProvider>
        <HightouchBillingQueryContextProvider>
          <Switch>
            <Route exact path="/payments/info">
              <BillingPage />
            </Route>
            <Route exact path="/payments/info/messagingUsage">
              <MessagingUsage />
            </Route>
            <Route exact path="/payments/info/dataUsage">
              <CustomEventsUsage />
            </Route>
            <Route exact path="/payments/info/journeyEventsUsage">
              <JourneyEventsUsage />
            </Route>
            <Route exact path="/payments/info/journeyEventsUsage/:projectId">
              <JourneyEventsUsageByProject />
            </Route>
            <Route exact path="/payments/info/audienceUsage">
              <UsersUsage />
            </Route>
            <Route exact path="/payments/info/catalogUsage">
              <CatalogUsage />
            </Route>
            <Route exact path="/payments/info/smartIngestUsage">
              <SmartIngestUsage />
            </Route>
          </Switch>
        </HightouchBillingQueryContextProvider>
      </BillingQueryContextProvider>
    </UsageLimitsContextProvider>
  );
};

export const Billing = () => {
  const isBillingLimitsPreviewEnabled = useAllowPreview(USAGE_LIMITS_PREVIEW_KEY);
  const isBillingLimitsFeatureFlagEnabled = useFeatureFlag(USAGE_LIMITS_FF);
  const isBillingLimitsEnabled = isBillingLimitsPreviewEnabled || isBillingLimitsFeatureFlagEnabled;

  return isBillingLimitsEnabled ? <BillingWithUsageLimits /> : <BillingWithoutUsageLimits />;
};
